import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import {
  formatCurrency, formatDateShort, formatRelative,
  DISCIPLINE_LABEL,
} from "@/lib/utils";
import HealthScore from "@/components/HealthScore";
import HealthDetailsModal from "@/components/HealthDetailsModal";
import InternalBadge from "@/components/InternalBadge";
import SurveyControlTile from "@/components/SurveyControlTile";
import ProjectInbox from "@/components/ProjectInbox";
import ProjectEmailCard from "@/components/intake/ProjectEmailCard";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, MapPin, Briefcase, Calendar, Layers, Search, FileText, AlertOctagon, Activity as ActivityIcon, FileDown, Share2, Check, Copy, Lock, Plus, Upload, Settings2, History, Package as PackageIcon, CheckSquare, Archive, Compass, Download } from "lucide-react";
import { StatusBadge, PriorityBadge } from "@/components/Badges";
import { api as apiClient, sheetFileUrl } from "@/lib/api";
import UploadSheetsModal from "@/components/admin/UploadSheetsModal";
import { canAdminPlans } from "@/lib/permissions";
import { TrustBadge } from "@/components/TrustBadge";
import { ProjectTrustSummary } from "@/components/ProjectTrustSummary";
import OfflinePackageModal from "@/components/OfflinePackageModal";

const downloadUrlFromFile = (fileRel) => {
  const t = window.localStorage.getItem("fop_bearer");
  return `${process.env.REACT_APP_BACKEND_URL}/api/exports/file/${fileRel}${t ? `?token=${t}` : ""}`;
};

function ExportPanel({ project }) {
  const [packages, setPackages] = useState([]);
  const [pkgKey, setPkgKey] = useState("coordination_report");
  const [createShare, setCreateShare] = useState(true);
  const [ttlHours, setTtlHours] = useState(168);
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/exports/packages", { params: { scope: "project" } });
        setPackages(data);
      } catch { /* ignore: package list is non-critical */ }
    })();
  }, []);

  const selectedPkg = packages.find((p) => p.key === pkgKey);

  const run = async () => {
    setExporting(true);
    setExportResult(null);
    try {
      const params = { share: createShare, ttl_hours: ttlHours };
      const { data } = await api.post(`/projects/${project.id}/exports/${pkgKey}`, null, { params });
      setExportResult(data);
    } finally {
      setExporting(false);
    }
  };

  const previewUrl = exportResult?.share?.preview_url
    ? `${process.env.REACT_APP_BACKEND_URL}${exportResult.share.preview_url}`
    : null;

  const copyShare = async () => {
    if (!previewUrl) return;
    try { await navigator.clipboard.writeText(previewUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_e) { /* clipboard not available */ }
  };

  return (
    <div className="fop-surface-2 rounded-lg p-5 mb-4" data-testid="export-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">Operational Deliverables</div>
          <h3 className="font-display text-lg text-[var(--fop-text)]">{selectedPkg?.label || "Export Package"}</h3>
          <p className="text-xs text-[var(--fop-text-3)] mt-1 max-w-xl">
            {selectedPkg?.description || "Branded PDF deliverable — claim-grade, tenant-branded, share-portal ready."}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <select
            value={pkgKey}
            onChange={(e) => { setPkgKey(e.target.value); setExportResult(null); }}
            className="text-xs py-1.5 px-2 font-mono bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded min-w-[14rem]"
            data-testid="export-package-select"
          >
            {packages.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}{p.stub ? "  ·  (stub)" : ""}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs text-[var(--fop-text-2)] cursor-pointer" data-testid="export-share-toggle-label">
              <input
                type="checkbox"
                checked={createShare}
                onChange={(e) => setCreateShare(e.target.checked)}
                className="cursor-pointer"
                data-testid="export-share-toggle"
              />
              Generate secure share link
            </label>
            {createShare && (
              <select
                value={ttlHours}
                onChange={(e) => setTtlHours(Number(e.target.value))}
                className="text-xs py-1 px-2 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded font-mono"
                data-testid="export-share-ttl"
              >
                <option value={24}>1 day</option>
                <option value={72}>3 days</option>
                <option value={168}>7 days</option>
                <option value={336}>14 days</option>
                <option value={720}>30 days</option>
              </select>
            )}
          </div>
          <button onClick={run} disabled={exporting} className="btn btn-primary text-sm" data-testid="export-generate">
            <FileDown size={14} /> {exporting ? "Generating…" : "Generate"}
          </button>
        </div>
      </div>

      {exportResult && (
        <div className="mt-4 px-3 py-2.5 rounded-md border border-[var(--fop-border)] bg-[var(--fop-surface)] flex items-center gap-3 flex-wrap" data-testid="export-result">
          <span className="font-mono text-[11px] text-[var(--fop-text-2)] truncate">{exportResult.filename}</span>
          <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-4)]">
            {Math.round(exportResult.size_bytes / 1024)} KB
          </span>
          <div className="ml-auto flex items-center gap-2">
            <a
              href={downloadUrlFromFile(exportResult.file_url)}
              target="_blank" rel="noreferrer"
              className="btn btn-ghost text-xs"
              data-testid="export-download"
            >
              <FileDown size={12} /> Download
            </a>
            {previewUrl && (
              <>
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs underline text-[var(--fop-primary)]"
                  data-testid="export-share-open"
                >
                  Open preview
                </a>
                <input value={previewUrl} readOnly className="text-xs py-1 w-72 font-mono" data-testid="export-share-url" />
                <button onClick={copyShare} className="btn btn-ghost text-xs" data-testid="export-share-copy">
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Project() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [activities, setActivities] = useState([]);
  const [health, setHealth] = useState(null);
  const [healthOpen, setHealthOpen] = useState(false);
  const [discipline, setDiscipline] = useState("");
  const [q, setQ] = useState("");
  const [togglingInternal, setTogglingInternal] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [uploadMode, setUploadMode] = useState(null);   // "new" | "revision"
  const [showOfflinePkg, setShowOfflinePkg] = useState(false);
  // Iter 108 Phase 2 — Project Command Center: unify existing data sources
  // (no new endpoints, no new pages). Each field below already has a backend
  // computed from a single existing route.
  const [punchSummary, setPunchSummary] = useState(null);
  const [findingsCount, setFindingsCount] = useState(null);
  const [packagesCount, setPackagesCount] = useState(null);
  const [latestUpload, setLatestUpload] = useState(null);
  const [watching, setWatching] = useState(false);
  // PILLAR 1 — per-sheet trust badges. One round-trip for the whole list.
  const [sheetTrust, setSheetTrust] = useState({}); // { sheet_id: { state, label } }
  const showAdmin = canAdminPlans(user);
  const refreshSheets = () => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/sheets`).then((r) => setSheets(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get(`/projects/${projectId}`).then((r) => setProject(r.data));
    api.get(`/projects/${projectId}/health`).then((r) => setHealth(r.data));
    api.get(`/activities`, { params: { project_id: projectId, limit: 20 } }).then((r) => setActivities(r.data));
    // Iter 108 Phase 2 — fetch the rest of the Command Center data from
    // existing endpoints. Failures are silent and degrade the tile gracefully.
    api.get(`/projects/${projectId}/punch-items/summary`)
      .then((r) => setPunchSummary(r.data)).catch(() => {});
    api.get(`/discoveries`, { params: { project_id: projectId } })
      .then((r) => {
        const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        const open = items.filter((x) => (x.status || "").toLowerCase() !== "resolved").length;
        setFindingsCount({ total: items.length, open });
      }).catch(() => {});
    api.get(`/sheet-packages`, { params: { project_id: projectId } })
      .then((r) => {
        const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setPackagesCount(items.length);
      }).catch(() => {});
    api.get(`/projects/${projectId}/upload_batches`, { params: { limit: 1 } })
      .then((r) => {
        const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        if (items.length > 0) setLatestUpload(items[0]);
      }).catch(() => {});
    api.get(`/watchlists`, { params: { project_id: projectId } })
      .then((r) => {
        const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setWatching(items.some((w) => w.user_id === user?.id || w.is_following === true) || items.length > 0);
      }).catch(() => {});
  }, [projectId, user?.id]);

  const toggleInternal = async () => {
    if (!project || togglingInternal) return;
    const next = !project.internal;
    if (next && !window.confirm(
      "Mark this project INTERNAL?\n\n" +
      "• Plans will use raw, unsanitized sources on next reload\n" +
      "• Public share links will be blocked\n" +
      "• Only admins can toggle this flag",
    )) return;
    setTogglingInternal(true);
    try {
      const { data } = await api.patch(`/projects/${projectId}`, { internal: next });
      setProject(data);
    } catch (e) {
      window.alert("Failed to toggle internal flag: " + (e?.response?.data?.detail || e.message));
    } finally {
      setTogglingInternal(false);
    }
  };

  // Iter 135 — customer-facing archive. Soft-archives the project so it
  // disappears from the default portfolio while files/audit/history are
  // preserved. Hard delete is intentionally not exposed here.
  const archiveProject = async () => {
    if (!project || archiving) return;
    if (!window.confirm(
      "Archive this project?\n\n" +
      "It will disappear from your active project list, but files and " +
      "history are preserved. An admin can restore it later.",
    )) return;
    setArchiving(true);
    try {
      await api.patch(`/projects/${projectId}/archive`);
      window.alert("Project archived. Files and history were preserved.");
      navigate("/portfolio");
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      if (msg === "project_already_archived") {
        window.alert("This project is already archived.");
        navigate("/portfolio");
      } else {
        window.alert("Failed to archive project: " + msg);
      }
    } finally {
      setArchiving(false);
    }
  };

  useEffect(() => {
    const params = {};
    if (discipline) params.discipline = discipline;
    if (q) params.q = q;
    api.get(`/projects/${projectId}/sheets`, { params }).then((r) => setSheets(r.data));
  }, [projectId, discipline, q]);

  // PILLAR 1 — Sheet trust badge data. Single round-trip per project.
  useEffect(() => {
    if (!projectId) return;
    api
      .get(`/projects/${projectId}/sheet-trust`)
      .then((r) => {
        const map = {};
        for (const row of r.data?.rows || []) {
          map[row.sheet_id] = { state: row.state, label: row.label };
        }
        setSheetTrust(map);
      })
      .catch(() => {});
  }, [projectId, sheets.length]);

  if (!project) return <div className="p-8 font-mono text-xs text-[var(--fop-text-3)]">Loading…</div>;

  const disciplines = project.disciplines || [];

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="project-page">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <Link to="/portfolio" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)]">
          <ArrowLeft size={12} /> All Projects
        </Link>
        {showAdmin && (
          <div className="flex items-center gap-2" data-testid="project-admin-actions">
            <button
              type="button"
              onClick={() => setUploadMode("new")}
              className="btn btn-ghost inline-flex items-center gap-1.5 text-xs"
              data-testid="project-add-sheets-btn"
            >
              <Plus size={12} /> Add sheets
            </button>
            <button
              type="button"
              onClick={() => setUploadMode("revision")}
              className="btn btn-ghost inline-flex items-center gap-1.5 text-xs"
              data-testid="project-upload-revision-btn"
            >
              <Upload size={12} /> Upload revision
            </button>
            <button
              type="button"
              onClick={() => {
                const target = document.querySelector('[data-testid="sheets-panel"]');
                if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="btn btn-ghost inline-flex items-center gap-1.5 text-xs"
              data-testid="project-manage-planset-btn"
              title="Jump to plan set"
            >
              <Settings2 size={12} /> Manage plan set
            </button>
            <Link
              to={`/project/${project.id}/revisions`}
              className="btn btn-ghost inline-flex items-center gap-1.5 text-xs"
              data-testid="project-revisions-btn"
              title="Revision history & compare"
            >
              <History size={12} /> Revisions
            </Link>
            <Link
              to={`/project/${project.id}/packages`}
              className="btn btn-ghost inline-flex items-center gap-1.5 text-xs"
              data-testid="project-packages-btn"
              title="Sheet packages"
            >
              <PackageIcon size={12} /> Packages
            </Link>
            <button
              type="button"
              onClick={() => setShowOfflinePkg(true)}
              className="btn btn-ghost inline-flex items-center gap-1.5 text-xs"
              data-testid="project-offline-package-btn"
              title="Download this project for offline use"
            >
              <Download size={12} /> Offline
            </button>
            {/* Iter 107 P0B — Closeout Center recovered from orphan list.
                Reachable from the project page where PMs naturally finish
                a job. No separate nav entry. */}
            <Link
              to={`/project/${project.id}/closeout`}
              className="btn btn-ghost inline-flex items-center gap-1.5 text-xs"
              data-testid="project-closeout-btn"
              title="Closeout deliverables & handover"
            >
              <CheckSquare size={12} /> Closeout
            </Link>
            <Link
              to={`/project/${project.id}/survey-control`}
              className="btn btn-ghost inline-flex items-center gap-1.5 text-xs"
              data-testid="project-survey-control-btn"
              title="Project coordinate system, control points & benchmarks"
            >
              <Compass size={12} /> Survey Control
            </Link>
            <button
              type="button"
              onClick={archiveProject}
              disabled={archiving || (project?.status || "").toLowerCase() === "archived"}
              className="btn btn-ghost inline-flex items-center gap-1.5 text-xs text-[var(--fop-text-3)] hover:text-[var(--st-escalated)] disabled:opacity-50"
              data-testid="project-archive-btn"
              title="Archive this project (files and history are preserved)"
            >
              <Archive size={12} /> {archiving ? "Archiving…" : "Archive"}
            </button>
          </div>
        )}
      </div>

      {/* Iter 108 Phase 2 — Project Command Center
          One header. One glance. Every key number from existing endpoints.
          No new endpoints. No new pages. */}
      <div className="fop-surface-2 p-6 rounded-lg mb-4 relative overflow-hidden" data-testid="project-command-center">
        <div className="absolute top-0 left-0 right-0 h-1" style={{ background: project.cover_color || "var(--fop-primary)" }} />
        <div className="flex flex-wrap gap-6 items-start">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--fop-text-3)]">
                {project.number}
              </div>
              {project.internal && <InternalBadge size="sm" />}
              {(() => {
                const ph = (project.phase || project.status || "active").toLowerCase();
                const isClosed = ph === "closed" || ph === "completed" || ph === "archived";
                return (
                  <span
                    data-testid="project-status-pill"
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border"
                    style={isClosed
                      ? { borderColor: "var(--fop-border)", color: "var(--fop-text-3)" }
                      : { borderColor: "rgba(56,161,105,0.4)", color: "rgb(56,161,105)" }}
                  >
                    {isClosed ? "Closed" : "Active"}
                  </span>
                );
              })()}
              {health && (health.score != null) && (
                <button
                  type="button"
                  onClick={() => setHealthOpen(true)}
                  data-testid="project-health-pill"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border border-[var(--fop-border)] text-[var(--fop-text-3)] hover:text-[var(--fop-text)] hover:border-[var(--fop-border-hi)] transition-colors"
                  title="Project coordination health"
                >
                  Health · {health.score}
                  {health.trend === "up" && <span className="text-[rgb(56,161,105)]">▲</span>}
                  {health.trend === "down" && <span className="text-[var(--st-escalated)]">▼</span>}
                </button>
              )}
              {watching && (
                <span data-testid="project-watch-pill" className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border border-[var(--fop-primary)] text-[var(--fop-primary)]">
                  ● Watching
                </span>
              )}
              {user?.role === "admin" && (
                <button
                  onClick={toggleInternal}
                  disabled={togglingInternal}
                  data-testid="project-internal-toggle"
                  className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider border border-[var(--fop-border)] text-[var(--fop-text-3)] hover:text-[var(--fop-text)] hover:border-[var(--fop-border-hi)] transition-colors disabled:opacity-50"
                  title={project.internal ? "Disable internal flag (re-enables sharing)" : "Mark as internal-only (admin tooling)"}
                >
                  <Lock size={9} strokeWidth={2.2} />
                  {project.internal ? "Unmark internal" : "Mark internal"}
                </button>
              )}
            </div>
            <h1 className="font-display text-3xl text-[var(--fop-text)] mb-3 leading-tight">
              {project.name}
            </h1>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm text-[var(--fop-text-2)]">
              {project.client && <span className="inline-flex items-center gap-1.5"><Briefcase size={13} /> {project.client}</span>}
              {project.location && <span className="inline-flex items-center gap-1.5"><MapPin size={13} /> {project.location}</span>}
              <span className="inline-flex items-center gap-1.5"><Calendar size={13} /> {formatDateShort(project.start_date)} → {formatDateShort(project.end_date)}</span>
              {latestUpload && (
                <span data-testid="cc-last-upload" className="inline-flex items-center gap-1.5 text-[var(--fop-text-3)]" title={`Latest upload batch: ${latestUpload.id}`}>
                  <Upload size={13} /> Last upload {formatRelative(latestUpload.created_at)}
                </span>
              )}
              {activities && activities[0] && (
                <span data-testid="cc-last-activity" className="inline-flex items-center gap-1.5 text-[var(--fop-text-3)]" title={activities[0].message}>
                  <ActivityIcon size={13} /> Last activity {formatRelative(activities[0].created_at)}
                </span>
              )}
            </div>
            {project.pressure_tag && (
              <div className="mt-4 inline-block px-3 py-1.5 rounded-md border border-[rgba(232, 93, 31,0.25)] bg-[var(--fop-primary-soft)] text-[var(--fop-primary)] text-xs font-medium">
                ⚡ {project.pressure_tag}
              </div>
            )}
            {project.pressure_notes && (
              <p className="text-xs text-[var(--fop-text-3)] mt-2 max-w-3xl">{project.pressure_notes}</p>
            )}
          </div>

          {/* Command-center KPI grid: 2x4 = 8 numbers, all from existing endpoints */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 min-w-[360px]" data-testid="cc-kpi-grid">
            <Stat label="Sheets" value={project.kpis.sheets} accent="var(--fop-text-2)" />
            <Stat label="Current Rev" value={(() => {
              if (!sheets || sheets.length === 0) return "—";
              return "R" + sheets.reduce((m, s) => Math.max(m, s.revision || 0), 0);
            })()} accent="var(--fop-text-2)" />
            <Stat label="Open RFIs" value={project.kpis.open_rfis} accent="var(--st-assigned)" />
            <Stat label="Open Punch" value={punchSummary ? punchSummary.open : "—"} accent="var(--st-open)" testid="cc-stat-open-punch" />
            <Stat label="Open Pins" value={project.kpis.open_pins} accent="var(--st-open)" />
            <Stat label="Safety" value={project.kpis.safety} accent="var(--st-escalated)" />
            <Stat label="Findings" value={findingsCount ? findingsCount.open : "—"} accent="var(--st-assigned)" testid="cc-stat-findings" />
            <Stat label="Packages" value={packagesCount ?? "—"} accent="var(--fop-text-2)" testid="cc-stat-packages" />
          </div>
        </div>
      </div>

      {/* Iter EXP-4 — Morning Roll-Call rail.
          Replaces the dashboard mentality with an operations mentality.
          Reframes the same data the KPI grid already has, but as
          imperatives: "What needs attention right now?" Each chip is a
          single tap to the relevant page. Hidden when nothing needs
          action so it never wastes screen real-estate. */}
      <MorningRollCall
        project={project}
        sheets={sheets}
        punchSummary={punchSummary}
        findingsCount={findingsCount}
        activities={activities}
      />

      {/* SC-1 — Survey Control shortcut tile. Honest RED/AMBER/GREEN status
          so the operator can tell at a glance if the project is ready for
          field positioning. Clicks through to /project/{id}/survey-control. */}
      <div className="mb-4">
        <SurveyControlTile projectId={project.id} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        {/* Sheets section */}
        <div className="space-y-4">
          {/* ExportPanel relocated behind admin Deliverables — drawing-first project page */}
        <div className="fop-surface-2 rounded-lg p-5" data-testid="sheets-panel">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">Plan Set</div>
              <h2 className="font-display text-xl text-[var(--fop-text)]">100% Issued for Construction</h2>
            </div>
            <div className="flex gap-2 items-center">
              <Link
                to={`/project/${project.id}/plans`}
                className="btn btn-solid text-xs"
                data-testid="open-plans-browser"
                title="Open the visual plans browser"
              >
                <Layers size={13} /> Browse plans
              </Link>
              <Link
                to={`/project/${project.id}/documents/intake`}
                className="btn btn-ghost text-xs"
                data-testid="open-document-intake"
                title="Upload documents and link them to drawing objects"
              >
                <FileText size={13} /> Upload Documents
              </Link>
              <div className="relative">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--fop-text-3)]" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Sheet # or title"
                  className="pl-8 py-1.5 text-xs w-56"
                  data-testid="sheet-search"
                />
              </div>
              <select
                value={discipline}
                onChange={(e) => setDiscipline(e.target.value)}
                className="py-1.5 text-xs w-40"
                data-testid="discipline-filter"
              >
                <option value="">All disciplines</option>
                {disciplines.map((d) => (
                  <option key={d} value={d}>{DISCIPLINE_LABEL[d] || d}</option>
                ))}
              </select>
            </div>
          </div>

          {sheets.length === 0 ? (
            (q || discipline) ? (
              <div
                className="text-[var(--fop-text-3)] text-sm py-10 text-center"
                data-testid="project-sheets-no-match"
              >
                No sheets match your search.
              </div>
            ) : (
              <div
                className="border border-dashed border-[var(--fop-border-2)] rounded-md p-8 text-center"
                data-testid="project-sheets-empty-state"
              >
                <FileText size={28} className="mx-auto text-[var(--fop-text-3)] mb-3" />
                <div className="font-display text-lg text-[var(--fop-text)] mb-1">
                  No sheets in this project yet.
                </div>
                <div className="text-sm text-[var(--fop-text-2)] mb-4 max-w-md mx-auto">
                  Drop PDF sheets in to start the plan set. We parse every
                  page, build thumbnails, and the team is on it the same day.
                </div>
                {showAdmin ? (
                  <button
                    type="button"
                    onClick={() => setUploadMode("new")}
                    className="btn btn-primary inline-flex items-center gap-1.5 min-h-[44px] px-5"
                    data-testid="project-sheets-empty-upload"
                  >
                    <Upload size={14} /> Upload sheets
                  </button>
                ) : (
                  <div className="text-xs text-[var(--fop-text-3)] font-mono uppercase tracking-wider">
                    Waiting for the project administrator to upload sheets.
                  </div>
                )}
              </div>
            )
          ) : (
            <div className="divide-y divide-[var(--fop-border)]">
              {sheets.map((s) => (
                <Link
                  key={s.id}
                  to={`/sheet/${s.id}`}
                  className="grid grid-cols-[100px_1fr_140px_60px_60px] gap-3 items-center py-2.5 px-2 -mx-2 rounded hover:bg-[var(--fop-surface)] transition-colors group"
                  data-testid={`sheet-row-${s.sheet_number}`}
                >
                  <div className="font-mono text-sm font-semibold text-[var(--fop-text)] group-hover:text-[var(--fop-primary)]">
                    {s.sheet_number}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm text-[var(--fop-text)] truncate">{s.sheet_title}</div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                        {DISCIPLINE_LABEL[s.discipline] || s.discipline}
                      </span>
                      {sheetTrust[s.id] && (
                        <TrustBadge
                          state={sheetTrust[s.id].state}
                          label={sheetTrust[s.id].label}
                          testid={`sheet-trust-${s.sheet_number}`}
                        />
                      )}
                    </div>
                  </div>
                  <div className="text-[11px] font-mono text-[var(--fop-text-3)]">
                    REV {s.revision}{s.revision_date ? ` · ${formatDateShort(s.revision_date)}` : ""}
                  </div>
                  <div className="text-center">
                    {s.open_pins > 0 ? (
                      <span className="pill st-open">{s.open_pins}</span>
                    ) : (
                      <span className="text-[var(--fop-text-4)] font-mono text-xs">—</span>
                    )}
                  </div>
                  <div className="text-right">
                    <FileText size={14} className="inline text-[var(--fop-text-3)] group-hover:text-[var(--fop-primary)]" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
        </div>{/* end left column wrapper */}

        {/* Activity sidebar */}
        <div className="space-y-4 self-start">
        {/* PILLAR 1 — Project Trust Summary. The first thing the PM/superintendent
            should see on every visit. Plain-English verdict + one tap to act. */}
        <ProjectTrustSummary projectId={project.id} />
        {/* Iter 95 — Project Upload Email card */}
        <ProjectEmailCard projectId={project.id} projectName={project.name} />
        {/* Iter 83.2B-Polish — Project Inbox above Activity in the right column */}
        <ProjectInbox projectId={project.id} compact={true} />
        <div className="fop-surface-2 rounded-lg p-5" data-testid="activity-panel">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-1">Activity</div>
          <h3 className="font-display text-lg text-[var(--fop-text)] mb-4">Recent operations</h3>
          {activities.length === 0 ? (
            <div className="text-[var(--fop-text-3)] text-sm">No recent activity.</div>
          ) : (
            <ul className="space-y-3">
              {activities.map((a) => (
                <li key={a.id} className="flex gap-2.5">
                  <div className="w-1 self-stretch rounded-full bg-[var(--fop-primary)] opacity-60" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-[var(--fop-text)] leading-snug">{a.message}</div>
                    <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-4)] mt-0.5">
                      {formatRelative(a.created_at)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        </div>
      </div>

      {healthOpen && (
        <HealthDetailsModal
          projectId={project.id}
          projectName={project.name}
          onClose={() => setHealthOpen(false)}
        />
      )}
      {uploadMode && (
        <UploadSheetsModal
          projectId={project.id}
          projectName={project.name}
          mode={uploadMode}
          onClose={() => setUploadMode(null)}
          onUploaded={refreshSheets}
        />
      )}
      {showOfflinePkg && (
        <OfflinePackageModal
          projectId={project.id}
          onClose={() => setShowOfflinePkg(false)}
        />
      )}
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  return (
    <div className="rounded-md border border-[var(--fop-border)] bg-[var(--fop-surface)] px-3 py-2.5" data-testid={testid}>
      <div className="text-2xl font-bold font-mono leading-none" style={{ color: accent }}>{value}</div>
      <div className="text-[10px] uppercase font-mono tracking-wider text-[var(--fop-text-3)] mt-1.5">{label}</div>
    </div>
  );
}


/* ============================================================
 * Iter EXP-4 — Morning Roll-Call
 *
 * Reframes the project page from "dashboard" to "operations."
 * Surfaces the imperatives — what needs attention right now —
 * in a single horizontal rail above the KPI grid. Each chip is
 * one tap to the page where action is taken.
 *
 * Hidden when nothing needs action.
 * ============================================================ */
function MorningRollCall({ project, sheets, punchSummary, findingsCount, activities }) {
  const items = [];
  // Open RFIs
  if (project?.kpis?.open_rfis > 0) {
    items.push({
      key: "rfis",
      label: `${project.kpis.open_rfis} open RFI${project.kpis.open_rfis === 1 ? "" : "s"}`,
      tone: "warn",
      href: `/project/${project.id}/rfis`,
    });
  }
  // Open Punch
  if (punchSummary?.open > 0) {
    items.push({
      key: "punch",
      label: `${punchSummary.open} punch item${punchSummary.open === 1 ? "" : "s"} open`,
      tone: "warn",
      href: `/project/${project.id}/punch`,
    });
  }
  // Open Findings (QA / inspector)
  if (findingsCount?.open > 0) {
    items.push({
      key: "findings",
      label: `${findingsCount.open} open finding${findingsCount.open === 1 ? "" : "s"}`,
      tone: "warn",
      href: `/project/${project.id}/findings`,
    });
  }
  // Safety / safety pressure
  if (project?.kpis?.safety > 0) {
    items.push({
      key: "safety",
      label: `${project.kpis.safety} safety alert${project.kpis.safety === 1 ? "" : "s"}`,
      tone: "danger",
      href: `/project/${project.id}/safety`,
    });
  }
  // Recent activity hint (informational only; only show on slow days)
  if (items.length === 0 && activities && activities[0]) {
    items.push({
      key: "calm",
      label: "All clear — no open actions",
      tone: "good",
      href: `/project/${project.id}/activity`,
    });
  }

  if (items.length === 0) return null;

  const TONE = {
    danger: { dot: "bg-red-500", text: "text-red-300", border: "border-red-500/30", bg: "bg-red-500/10" },
    warn:   { dot: "bg-amber-500", text: "text-amber-300", border: "border-amber-500/30", bg: "bg-amber-500/10" },
    good:   { dot: "bg-emerald-500", text: "text-emerald-300", border: "border-emerald-500/30", bg: "bg-emerald-500/10" },
    info:   { dot: "bg-cyan-500", text: "text-cyan-300", border: "border-cyan-500/30", bg: "bg-cyan-500/10" },
  };

  return (
    <div
      className="mb-4 rounded-xl border border-[var(--fop-border-2)] bg-[var(--fop-surface)] px-4 py-3"
      data-testid="morning-roll-call"
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="font-display text-base font-bold text-[var(--fop-text)] leading-none">
          Today's Work
        </div>
        <div className="text-xs text-[var(--fop-text-3)] leading-none">
          · what needs your attention right now
        </div>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="morning-roll-items">
        {items.map((it) => {
          const tone = TONE[it.tone] || TONE.info;
          return (
            <a
              key={it.key}
              href={it.href}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border ${tone.border} ${tone.bg} ${tone.text} font-bold text-sm transition-colors hover:bg-opacity-25`}
              data-testid={`morning-roll-item-${it.key}`}
            >
              <span className={`inline-block w-2 h-2 rounded-full ${tone.dot}`} aria-hidden="true" />
              {it.label}
            </a>
          );
        })}
      </div>
    </div>
  );
}
